/* =========================================================
 * TF CART PAGE (DOM PATCH + AUTO RERENDER TBODY)
 * - No F5 needed
 * - Key-based patch (no row disappearing)
 * - Gift = product variant (sync with mini cart)
 * - Updates totals, subtotals, qty, remove, bubble, note
 * - Auto re-render tbody when items added/removed (e.g. gift)
 * - Broadcast tf:cart:updated for mini cart sync
 * ========================================================= */

(() => {
  if (window.__TF_CART_PAGE_V1__) return;
  window.__TF_CART_PAGE_V1__ = true;

  const routes = window.routes || {
    cart_change_url: "/cart/change.js",
    cart_update_url: "/cart/update.js",
    cart_js_url: "/cart.js",
  };

  const rootSelector = ".s-shop-cart.each-list-prd";

  /* -----------------------
   * Utils
   * --------------------- */
  const esc = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const getRoot = (el) => el.closest(rootSelector);

  const setLoading = (root, state) => {
    if (!root) return;
    root.classList.toggle("is-loading", !!state);
  };

  const fmtMoney = (cents) => {
    // 1) Store money format passed from main-cart.liquid (e.g. "Rs. {{amount}}")
    if (typeof window.formatCartMoney === "function") {
      return window.formatCartMoney(Number(cents || 0));
    }
    // 2) Shopify helper, if theme exposes it
    const fmt = (window.theme && window.theme.moneyFormat) || (window.Shopify && window.Shopify.money_format);
    if (fmt && window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(Number(cents || 0), fmt);
    }
    // 3) Fallback: INR (was hardcoded USD -> showed "US$")
    return (Number(cents || 0) / 100).toLocaleString("en-IN", {
      style: "currency",
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "INR",
    });
  };

  const fetchCart = async () => {
    const res = await fetch(routes.cart_js_url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    shownIfBad(res, "GET /cart.js");
    if (!res.ok) throw new Error("cart.js failed");
    return res.json();
  };

  const postJSON = async (url, payload) => {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
    shownIfBad(res, `POST ${url}`);
    if (!res.ok) throw new Error("cart request failed");
    return res.json();
  };

  function shownIfBad(res, label) {
    if (res.ok) return;
    // best-effort debug without breaking UX
    console.warn("[TF Cart]", label, "failed:", res.status, res.statusText);
  }

  /* -----------------------
   * Header bubble (global)
   * --------------------- */
  const patchCartBubble = (cart) => {
    const bubbleWrap =
      document.querySelector("#cart-icon-bubble .cart-count-bubble") ||
      document.querySelector(".cart-count-bubble");

    const qtyEl =
      document.querySelector("#cart-icon-bubble .cart-qty") ||
      document.querySelector("#cart-icon-bubble .qty") ||
      document.querySelector(".cart-count-bubble .cart-qty, .cart-count-bubble .qty");

    if (!bubbleWrap || !qtyEl) return;

    const count = Number(cart?.item_count || 0);

    if (count > 0) {
      bubbleWrap.style.display = "";
      qtyEl.textContent = String(count);
      qtyEl.setAttribute("aria-label", String(count));
    } else {
      bubbleWrap.style.display = "none";
      qtyEl.textContent = "0";
      qtyEl.setAttribute("aria-label", "0");
    }
  };

  /* -----------------------
   * Gift helpers (variant)
   * --------------------- */
  const findGiftItemByVariant = (cart, variantId) =>
    (cart.items || []).find((it) => Number(it.variant_id) === Number(variantId)) || null;

  const patchGiftCheckbox = (cart) => {
    document.querySelectorAll("[data-gift-checkbox][data-gift-variant-id]").forEach((cb) => {
      const vid = Number(cb.getAttribute("data-gift-variant-id") || 0);
      if (!vid) return;
      cb.checked = !!findGiftItemByVariant(cart, vid);
    });
  };

  /* -----------------------
   * Build tbody rows from cart
   * (needed when adding/removing items e.g. gift)
   * --------------------- */
  const buildCartRowsHTML = (cart) => {
    const items = cart?.items || [];
    if (!items.length) {
      return `
        <tr>
          <td colspan="4" style="padding:24px 0;">Your cart is empty.</td>
        </tr>
      `;
    }

    // allow custom Remove text by optional data attr on cart root
    const root = document.querySelector(rootSelector);
    const removeText = root?.getAttribute("data-remove-text") || "Remove";

    return items
    .map((item, index) => {
        const line = index + 1; // 1-based
        const img = item.featured_image?.url || "";
        const imgUrl = img ? `${img}${img.includes("?") ? "&" : "?"}width=220` : "";

        const variantTitle =
          item.variant_title && item.variant_title !== "Default Title"
            ? `<p class="prd-type">${esc(item.variant_title)}</p>`
            : "";

        return `
          <tr class="file-delete order_item each-prd" data-key="${esc(item.key)}" data-line="${line}">
            <td>
              <div class="order_product">
                <a href="${esc(item.url)}" class="image">
                  ${
                    imgUrl
                      ? `<img src="${esc(imgUrl)}" alt="${esc(item.product_title)}" loading="lazy" width="110" height="110">`
                      : ""
                  }
                </a>
                <div class="infor">
                  <a href="${esc(item.url)}" class="prd-name h6 fw-normal link">
                    ${esc(item.product_title)}
                  </a>
                  ${variantTitle}
                </div>
              </div>
            </td>

            <td class="order_price each-price">${fmtMoney(item.final_price)}</td>

            <td>
              <div class="order_quantity">
                <div class="wg-quantity style-2">
                  <button
                    class="btn-quantity minus-quantity"
                    type="button"
                    aria-label="Decrease quantity"
                    data-quantity-change
                    data-line="${line}"
                    data-qty="${Math.max(0, item.quantity - 1)}"
                  ><i class="icon-minus"></i></button>

                  <input
                    class="quantity-product"
                    type="number"
                    min="0"
                    inputmode="numeric"
                    value="${item.quantity}"
                    aria-label="Quantity"
                    data-quantity-input
                    data-line="${line}"
                  >

                  <button
                    class="btn-quantity plus-quantity"
                    type="button"
                    aria-label="Increase quantity"
                    data-quantity-change
                    data-line="${line}"
                    data-qty="${item.quantity + 1}"
                  ><i class="icon-plus"></i></button>
                </div>

                <button class="remove tf-btn-line style-line-2 fw-normal" type="button" data-remove data-line="${line}">
                  ${esc(removeText)}
                </button>
              </div>
            </td>

            <td class="order_subtotal each-subtotal-price">${fmtMoney(item.final_line_price)}</td>
          </tr>
        `;
      })
      .join("");
  };

  /* -----------------------
   * Patch DOM (key-based)
   * - rerender tbody when mismatch
   * --------------------- */
  const patchDOM = (root, cart) => {
    if (!root || !cart) return;

    patchCartBubble(cart);
    patchGiftCheckbox(cart);

    // TOTAL
    const totalEl = root.querySelector(".each-total-price");
    if (totalEl) totalEl.textContent = fmtMoney(cart.total_price);

    // Disable checkout if empty
    const checkoutBtn = root.querySelector("[data-checkout]");
    if (checkoutBtn) checkoutBtn.disabled = cart.item_count === 0;

    // NOTE reflect (don’t overwrite while typing)
    const note = root.querySelector('[data-cart-note] textarea[name="note"]');
    if (note && document.activeElement !== note) note.value = cart.note || "";

    const tbody = root.querySelector("cart-items tbody");
    if (!tbody) return;

    // If item set changed (added/removed/reordered) -> rebuild tbody
    const domKeys = Array.from(tbody.querySelectorAll("tr[data-key]")).map((tr) => tr.getAttribute("data-key"));
    const cartKeys = (cart.items || []).map((it) => it.key);

    const mismatch =
      domKeys.length !== cartKeys.length ||
      cartKeys.some((k) => !domKeys.includes(k));

    if (mismatch) {
      tbody.innerHTML = buildCartRowsHTML(cart);
      return;
    }

    // Otherwise patch existing rows by key
    const itemsByKey = new Map((cart.items || []).map((it) => [it.key, it]));
    const rows = Array.from(tbody.querySelectorAll("tr[data-key]"));

    rows.forEach((row, index) => {
      const key = row.getAttribute("data-key");
      const item = itemsByKey.get(key);

      if (!item) {
        row.remove();
        return;
      }

      const line = index + 1;

      row.setAttribute("data-line", String(line));

      // qty input
      const qtyInput = row.querySelector("[data-quantity-input]");
      if (qtyInput) {
        qtyInput.value = item.quantity;
        qtyInput.setAttribute("data-line", String(line));
      }

      // buttons +/- update
      const minusBtn = row.querySelector(".minus-quantity[data-quantity-change]");
      const plusBtn = row.querySelector(".plus-quantity[data-quantity-change]");
      if (minusBtn) {
        minusBtn.setAttribute("data-line", String(line));
        minusBtn.setAttribute("data-qty", String(Math.max(0, item.quantity - 1)));
      }
      if (plusBtn) {
        plusBtn.setAttribute("data-line", String(line));
        plusBtn.setAttribute("data-qty", String(item.quantity + 1));
      }

      // remove button update
      const removeBtn = row.querySelector("[data-remove]");
      if (removeBtn) removeBtn.setAttribute("data-line", String(line));

      // prices
      const unitPrice = row.querySelector(".order_price.each-price");
      if (unitPrice) unitPrice.textContent = fmtMoney(item.final_price);

      const lineSubtotal = row.querySelector(".order_subtotal.each-subtotal-price");
      if (lineSubtotal) lineSubtotal.textContent = fmtMoney(item.final_line_price);
    });
  };

  /* -----------------------
   * Broadcast to other parts
   * --------------------- */
  const broadcast = (cart) => {
    try {
      window.dispatchEvent(new CustomEvent("tf:cart:updated", { detail: { cart } }));
    } catch (_) {}
  };

  /* -----------------------
   * CLICK: +/- remove checkout
   * --------------------- */
  document.addEventListener("click", async (e) => {
    const root = getRoot(e.target);
    if (!root) return;

    const checkoutBtn = e.target.closest("[data-checkout]");
    if (checkoutBtn) {
      const terms = root.querySelector("#agree-term");
      if (terms && !terms.checked) {
        terms.focus();
        terms.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      window.location.href = "/checkout";
      return;
    }

    const removeBtn = e.target.closest("[data-remove]");
    const changeBtn = e.target.closest("[data-quantity-change]");
    if (!removeBtn && !changeBtn) return;

    try {
      setLoading(root, true);

      if (removeBtn) {
        const line = Number(removeBtn.getAttribute("data-line"));
        if (!line) return;

        await postJSON(routes.cart_change_url, { line, quantity: 0 });
        const cart = await fetchCart();
        patchDOM(root, cart);
        broadcast(cart);
        return;
      }

      if (changeBtn) {
        const line = Number(changeBtn.getAttribute("data-line"));
        const qty = Number(changeBtn.getAttribute("data-qty"));
        if (!line || !Number.isFinite(qty)) return;

        await postJSON(routes.cart_change_url, { line, quantity: Math.max(0, qty) });
        const cart = await fetchCart();
        patchDOM(root, cart);
        broadcast(cart);
      }
    } catch (err) {
      console.error("[TF Cart] click error", err);
    } finally {
      setLoading(root, false);
    }
  });

  /* -----------------------
   * CHANGE: gift / qty input / note
   * --------------------- */
  let noteTimer = null;

  document.addEventListener("change", async (e) => {
    const root = getRoot(e.target);
    if (!root) return;

    // ===== GIFT (variant item) =====
    const giftCb = e.target.closest("[data-gift-checkbox][data-gift-variant-id]");
    if (giftCb) {
      const vid = Number(giftCb.getAttribute("data-gift-variant-id") || 0);
      if (!vid) return;

      try {
        setLoading(root, true);

        const cartNow = await fetchCart();
        const giftItem = findGiftItemByVariant(cartNow, vid);

        if (giftCb.checked) {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: vid, quantity: 1 }),
          });
          shownIfBad(res, "POST /cart/add.js");
          if (!res.ok) throw new Error("Add gift failed");
        } else {
          if (giftItem) {
            const res = await fetch("/cart/change.js", {
              method: "POST",
              credentials: "same-origin",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
            });
            shownIfBad(res, "POST /cart/change.js (remove gift)");
            if (!res.ok) throw new Error("Remove gift failed");
          }
        }

        const cartNew = await fetchCart();
        patchDOM(root, cartNew);
        broadcast(cartNew);
      } catch (err) {
        console.error("[TF Cart] gift error", err);
        const cartRollback = await fetchCart().catch(() => null);
        if (cartRollback) {
          patchDOM(root, cartRollback);
          broadcast(cartRollback);
        }
      } finally {
        setLoading(root, false);
      }
      return;
    }

    // ===== QTY input =====
    const qtyInput = e.target.closest("[data-quantity-input]");
    if (qtyInput) {
      try {
        const line = Number(qtyInput.getAttribute("data-line"));
        let qty = parseInt(qtyInput.value, 10);
        if (!Number.isFinite(qty) || qty < 0) qty = 0;

        setLoading(root, true);
        await postJSON(routes.cart_change_url, { line, quantity: qty });

        const cart = await fetchCart();
        patchDOM(root, cart);
        broadcast(cart);
      } catch (err) {
        console.error("[TF Cart] qty error", err);
      } finally {
        setLoading(root, false);
      }
      return;
    }

    // ===== NOTE debounce =====
    const noteArea = e.target.closest('[data-cart-note] textarea[name="note"]');
    if (noteArea) {
      clearTimeout(noteTimer);
      noteTimer = setTimeout(async () => {
        try {
          setLoading(root, true);
          await postJSON(routes.cart_update_url, { note: noteArea.value || "" });
          const cart = await fetchCart();
          patchDOM(root, cart);
          broadcast(cart);
        } catch (err) {
          console.error("[TF Cart] note error", err);
        } finally {
          setLoading(root, false);
        }
      }, 450);
    }
  });

  /* -----------------------
   * Listen sync from mini cart
   * --------------------- */
  window.addEventListener("tf:cart:updated", (e) => {
    const cart = e.detail?.cart;
    if (!cart) return;

    const root = document.querySelector(rootSelector);
    if (root) patchDOM(root, cart);
    else patchCartBubble(cart);
  });

  /* -----------------------
   * Init
   * --------------------- */
  fetchCart()
    .then((cart) => {
      const root = document.querySelector(rootSelector);
      if (root) patchDOM(root, cart);
    })
    .catch(() => {});

  console.log("[TF Cart] page patch ON (auto tbody rerender)");
})();
